/**
 * A location provider that uses shift-clicks (desktop) or long-press
 * (mobile) on the map to simulate bluedot position updates. Intended
 * for debug/testing when not physically at the venue.
 *
 * On start(), enables debug location mode on the map (disables boxZoom
 * so shift-click fires, and activates the long-press detector for mobile).
 * On stop(), restores normal map behavior.
 *
 * Follows the same { start(cb, err), stop() } interface as
 * WebGeoLocationProvider and SimLocationProvider.
 *
 * @param {Object} app - The application instance (needed for bus access)
 */
function MapShiftClickLocationProvider(app) {
  let unsubscribe = null;
  const log = app.log.sublog('MapShiftClickLocationProvider');

  /**
   * Start listening for shift-clicks / long-presses on the map.
   * Each interaction resolves the floor at the clicked point and
   * invokes the position callback with the same signature as other
   * location providers.
   *
   * @param {Function} cb - (timestamp, latitude, longitude, accuracy, clFloor, source)
   */
  function start(cb) {
    if (unsubscribe) {
      stop();
    }

    // Enable debug location mode: disables boxZoom, enables long-press
    app.bus.send('map/setDebugLocationMode', { enabled: true });

    log.info('Started - shift-click (desktop) or long-press (mobile) to set position');

    unsubscribe = app.bus.on('map/shiftClick', async ({ lat, lng, ord }) => {
      // Resolve the CL floor at the clicked point so bluedotMarker
      // can place the dot on the correct floor
      let clFloor = null;
      try {
        const floor = await app.bus.get('map/getFloorAt', { lat, lng, ordinal: ord });
        if (floor) {
          clFloor = floor.clfloor ?? null;
        }
      } catch (e) {
        log.warn('Could not resolve floor at click point', e);
      }

      log.info(`Position: ${lat}, ${lng} (ordinal: ${ord}, clFloor: ${clFloor})`);
      cb(Date.now(), lat, lng, 5, clFloor, 'ShiftClick');
    });
  }

  function stop() {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }

    // Restore normal map behavior
    app.bus.send('map/setDebugLocationMode', { enabled: false });

    log.info('Stopped');
  }

  return {
    start,
    stop,
  };
}

export default MapShiftClickLocationProvider;
